import { t } from "./i18n";

export type ReviewWord = { english: string; korean: string };
type ReviewListOptions = { entries: readonly ReviewWord[]; index: number; showTranslations?: boolean; pictionary?: boolean };
const escape = (value: string) => value.replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
const bounded = (index: number, count: number) => Math.min(Math.max(0, index), Math.max(0, count - 1));
const wrapped = (index: number, count: number) => count > 0 ? ((index % count) + count) % count : 0;

/** Picture Detectives and the standalone tool own their data; this module owns the shared review presentation. */
export const renderReviewNavigation = (index: number, count: number, pictionary = false): string => `
    <nav class="review-list-navigation ${pictionary ? "pictionary-review-navigation" : "vocabulary-review-navigation"}" aria-label="${escape(t("vocabularyReviewTool"))}">
        <button type="button" class="secondary-button" data-review-previous data-review-index="${wrapped(index - 1, count)}" ${count <= 1 ? "disabled" : ""}>${escape(t("previousWord"))}</button>
        <span data-review-count data-vocabulary-count aria-live="polite" aria-atomic="true">${count ? index + 1 : 0} / ${count}</span>
        <button type="button" class="secondary-button" data-review-next data-review-index="${wrapped(index + 1, count)}" ${count <= 1 ? "disabled" : ""}>${escape(t("nextVocabularyWord"))}</button>
    </nav>`;

export const renderReviewTable = ({ entries, index, showTranslations = true, pictionary = false }: ReviewListOptions): string => `
    <table class="review-list-table ${pictionary ? "pictionary-word-bank-table" : "vocabulary-review-table"}">
        <thead><tr><th scope="col">${escape(t("word"))}</th>${showTranslations ? `<th scope="col">${escape(t("translation"))}</th>` : ""}</tr></thead>
        <tbody>${entries.map((entry, row) => `<tr data-review-row="${row}" data-vocabulary-row="${row}" class="${row === index ? "is-current" : ""}">
            <td><button type="button" data-review-index="${row}" data-vocabulary-index="${row}" aria-pressed="${row === index}">${escape(entry.english)}</button></td>
            ${showTranslations ? `<td><span class="vocabulary-review-meaning">${escape(entry.korean)}</span></td>` : ""}</tr>`).join("")}</tbody>
    </table>`;

export const renderReviewList = (options: ReviewListOptions): string => `
    <div class="review-list-scroll is-centered ${options.pictionary ? "pictionary-word-bank-table-shell" : "vocabulary-review-scroll"}" data-review-scroll tabindex="0" role="region" aria-label="${escape(t("vocabularyReviewTool"))}">${renderReviewTable(options)}</div>`;

export const syncReviewSelection = (root: HTMLElement, index: number, count: number): void => {
    index = bounded(index, count);
    const counter = root.querySelector<HTMLElement>("[data-review-count]");
    if (counter) counter.textContent = `${count ? index + 1 : 0} / ${count}`;
    for (const [selector, delta] of [["[data-review-previous]", -1], ["[data-review-next]", 1]] as const) {
        const button = root.querySelector<HTMLButtonElement>(selector);
        if (button) { button.disabled = count <= 1; button.dataset.reviewIndex = String(wrapped(index + delta, count)); }
    }
    root.querySelectorAll<HTMLElement>("[data-review-row]").forEach(row => {
        const selected = Number(row.dataset.reviewRow) === index;
        row.classList.toggle("is-current", selected);
        row.querySelector("button")?.setAttribute("aria-pressed", String(selected));
    });
};

/** Room snapshots usually change only the selected index. Keep the actual list/focus/scroll nodes in that case. */
export const replaceReviewMarkup = (root: HTMLElement, markup: string): void => {
    const previous = root.querySelector<HTMLElement>("[data-review-scroll]");
    if (previous && markup.includes("data-review-scroll")) {
        const template = document.createElement("template");
        template.innerHTML = markup;
        const next = template.content.querySelector<HTMLElement>("[data-review-scroll]");
        if (next && previous.querySelector("table")?.textContent === next.querySelector("table")?.textContent) {
            const index = Number(next.querySelector<HTMLElement>(".is-current")?.dataset.reviewRow ?? 0);
            syncReviewSelection(root, index, next.querySelectorAll("[data-review-row]").length);
            if (document.activeElement instanceof HTMLButtonElement && root.contains(document.activeElement) && document.activeElement.disabled) previous.focus({ preventScroll: true });
            return;
        }
    }
    const focused = document.activeElement instanceof HTMLElement && root.contains(document.activeElement);
    const scrollTop = previous?.scrollTop ?? 0;
    root.innerHTML = markup;
    const next = root.querySelector<HTMLElement>("[data-review-scroll]");
    if (next) { next.scrollTop = scrollTop; if (focused && previous) next.focus({ preventScroll: true }); }
};

export const centerReviewWord = (root: HTMLElement, index: number, animate = true): void => {
    const scroller = root.querySelector<HTMLElement>("[data-review-scroll]");
    const row = scroller?.querySelector<HTMLElement>(`[data-review-row="${index}"]`);
    if (!scroller || !row || !scroller.clientHeight) return;
    const rowBounds = row.getBoundingClientRect();
    const scrollBounds = scroller.getBoundingClientRect();
    scroller.scrollTo({
        top: scroller.scrollTop + rowBounds.top - scrollBounds.top - scroller.clientTop - (scroller.clientHeight - rowBounds.height) / 2,
        behavior: animate && !window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "smooth" : "instant"
    });
};

export const reviewIndexForKey = (event: KeyboardEvent, index: number, count: number): number | null => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.isComposing || !count) return null;
    const direction = ["ArrowRight", "ArrowDown"].includes(event.key) ? 1 : ["ArrowLeft", "ArrowUp"].includes(event.key) ? -1 : 0;
    return event.key === "Home" ? 0 : event.key === "End" ? count - 1 : direction ? wrapped(index + direction, count) : null;
};

/** Recenter only after geometry changes; ordinary scrolling stays under the teacher's control. */
export const observeReviewGeometry = (root: HTMLElement, current: () => number | null): (() => void) => {
    let frame = 0;
    const resize = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => { const index = current(); if (index !== null) centerReviewWord(root, index, false); });
    };
    const observer = new ResizeObserver(resize);
    observer.observe(root);
    window.addEventListener("resize", resize);
    document.addEventListener("fullscreenchange", resize);
    return () => { observer.disconnect(); cancelAnimationFrame(frame); window.removeEventListener("resize", resize); document.removeEventListener("fullscreenchange", resize); };
};
